import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sun } from "lucide-react";
import { streamChat } from "../lib/api";

const sid = () => {
  let s = sessionStorage.getItem("synergy_chat_sid");
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2); sessionStorage.setItem("synergy_chat_sid", s); }
  return s;
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Ray from Synergy Solar. Ask me anything about rooftop solar, subsidies or savings in the Tricity — or say 'quote' to get started." },
  ]);
  const endRef = useRef(null);
  const sessionId = useRef(sid());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      await streamChat(sessionId.current, text, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm offline right now. Please call us or fill the quote form." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      <motion.button data-testid="chat-toggle" onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-forest text-cream shadow-2xl">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-window" initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-[70] flex h-[520px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/10 bg-obsidian/95 shadow-2xl backdrop-blur-2xl">
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold"><Sun className="h-5 w-5 text-obsidian" /></span>
              <div>
                <p className="font-heading font-bold text-cream">Ray · Solar Assistant</p>
                <p className="text-xs text-cream/50">Synergy Solar Inc.</p>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-gold text-obsidian" : "bg-white/8 text-cream/90"}`}>
                    {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="flex items-center gap-2 border-t border-white/10 p-3">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about solar…"
                className="flex-1 rounded-full bg-white/8 px-4 py-2.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:ring-2 focus:ring-gold" />
              <button data-testid="chat-send" onClick={send} disabled={busy}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-obsidian disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const Dot = ({ d = 0 }) => (
  <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d }}
    className="inline-block h-1.5 w-1.5 rounded-full bg-cream/70" />
);
